import { useState, useEffect, useRef } from 'react';
import { CheckCircle, AlertTriangle, XCircle, ChevronDown, ChevronUp, Plus, Trash2, Star } from 'lucide-react';
import api from '../../services/api';

const STATUS_CONFIG = {
    validated:  { icon: CheckCircle,   color: '#22c55e', label: 'Validé' },
    needs_work: { icon: AlertTriangle, color: '#f59e0b', label: 'À revoir' },
    rejected:   { icon: XCircle,       color: '#ef4444', label: 'Rejeté' },
};

export default function QuizReviewPanel({ question, user }) {
    const isAdmin = user?.role === 'admin';

    // Cache persistant entre les navigations (Map questionId → reviews[])
    const cache = useRef(new Map());

    const [reviews, setReviews] = useState(() => cache.current.get(question.id) ?? null);
    const [open, setOpen] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [form, setForm] = useState({
        reviewer: user?.prenom || '',
        status: 'validated',
        confidence: 7,
        comment: '',
    });

    // Fetch auto à chaque changement de question (si pas encore en cache)
    useEffect(() => {
        const cached = cache.current.get(question.id);
        if (cached !== undefined) {
            setReviews(cached);
            return;
        }

        let cancelled = false;
        api.post('/admin/questions.php', { action: 'get_question', question_source: 'official', id: question.id })
            .then(({ data }) => {
                if (cancelled) return;
                const fetched = data.data?.question?.reviews || [];
                cache.current.set(question.id, fetched);
                setReviews(fetched);
            })
            .catch(() => {
                if (!cancelled) {
                    cache.current.set(question.id, []);
                    setReviews([]);
                }
            });

        return () => { cancelled = true; };
    }, [question.id]);

    // Réinitialiser le formulaire et fermer à chaque changement de question
    useEffect(() => {
        setShowForm(false);
        setMsg(null);
    }, [question.id]);

    const updateReviews = (updated) => {
        cache.current.set(question.id, updated);
        setReviews(updated);
    };

    const handleAddReview = async () => {
        setSaving(true);
        setMsg(null);
        try {
            const { data } = await api.post('/admin/questions.php', {
                action: 'add_review',
                question_source: 'official',
                question_id: question.id,
                review: form,
            });
            if (data.success) {
                updateReviews(data.data.reviews);
                setShowForm(false);
                setForm(f => ({ ...f, status: 'validated', confidence: 7, comment: '' }));
            }
        } catch (err) {
            setMsg(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (reviewer) => {
        if (!confirm(`Supprimer la review de "${reviewer}" ?`)) return;
        try {
            const { data } = await api.post('/admin/questions.php', {
                action: 'delete_review',
                question_source: 'official',
                question_id: question.id,
                reviewer,
            });
            if (data.success) updateReviews(data.data.reviews);
        } catch (err) {
            setMsg(err.response?.data?.message || 'Erreur lors de la suppression');
        }
    };

    const count = reviews?.length ?? null;

    return (
        <div className="qrp-panel">
            <button className="qrp-toggle" onClick={() => setOpen(o => !o)}>
                <span className="qrp-toggle-left">
                    <span className="qrp-toggle-label">Reviews</span>
                    {count !== null && <span className="qrp-count">{count}</span>}
                    {reviews && reviews.length > 0 && (
                        <span className="qrp-status-icons">
                            {reviews.map((r, i) => {
                                const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.needs_work;
                                const Icon = cfg.icon;
                                return <Icon key={i} size={12} style={{ color: cfg.color }} />;
                            })}
                        </span>
                    )}
                </span>
                <span className="qrp-toggle-right">
                    <span className="qrp-question-id">#{question.id}</span>
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
            </button>

            {open && (
                <div className="qrp-body">
                    {reviews === null && <p className="qrp-loading">Chargement...</p>}

                    {reviews !== null && (
                        <>
                            {reviews.length === 0 && (
                                <p className="qrp-empty">Aucune review pour cette question.</p>
                            )}

                            <div className="qrp-list">
                                {reviews.map((rev, i) => {
                                    const cfg = STATUS_CONFIG[rev.status] || STATUS_CONFIG.needs_work;
                                    const Icon = cfg.icon;
                                    return (
                                        <div key={i} className="qrp-review-item">
                                            <div className="qrp-review-header">
                                                <Icon size={14} style={{ color: cfg.color }} />
                                                <strong>{rev.reviewer}</strong>
                                                <span className="qrp-badge" style={{ background: cfg.color }}>{cfg.label}</span>
                                                <span className="qrp-meta">
                                                    <Star size={11} />{rev.confidence}/10
                                                    {rev.date && <> · {rev.date.slice(0, 10)}</>}
                                                </span>
                                                {isAdmin && (
                                                    <button
                                                        className="qrp-delete-btn"
                                                        onClick={() => handleDelete(rev.reviewer)}
                                                        title="Supprimer cette review"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                )}
                                            </div>
                                            {rev.comment && <p className="qrp-comment">{rev.comment}</p>}
                                        </div>
                                    );
                                })}
                            </div>

                            {!showForm && (
                                <button className="qrp-add-btn" onClick={() => setShowForm(true)}>
                                    <Plus size={13} /> Ajouter une review
                                </button>
                            )}

                            {showForm && (
                                <div className="qrp-form">
                                    <div className="qrp-form-row">
                                        <div className="qrp-form-field">
                                            <label>Reviewer</label>
                                            <input
                                                type="text"
                                                value={form.reviewer}
                                                onChange={e => setForm(f => ({ ...f, reviewer: e.target.value }))}
                                            />
                                        </div>
                                        <div className="qrp-form-field">
                                            <label>Verdict</label>
                                            <select
                                                value={form.status}
                                                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                                            >
                                                <option value="validated">Validé</option>
                                                <option value="needs_work">À revoir</option>
                                                <option value="rejected">Rejeté</option>
                                            </select>
                                        </div>
                                        <div className="qrp-form-field qrp-form-field--narrow">
                                            <label>Confiance</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="10"
                                                value={form.confidence}
                                                onChange={e => setForm(f => ({ ...f, confidence: +e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                    <textarea
                                        className="qrp-form-comment"
                                        rows={2}
                                        value={form.comment}
                                        onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
                                        placeholder="Remarques sur la question..."
                                    />
                                    {msg && <p className="qrp-msg-error">{msg}</p>}
                                    <div className="qrp-form-actions">
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={handleAddReview}
                                            disabled={saving || !form.reviewer.trim()}
                                        >
                                            {saving ? 'Envoi...' : 'Enregistrer'}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => { setShowForm(false); setMsg(null); }}
                                        >
                                            Annuler
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
